import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Factory, ClipboardCheck, Boxes, Wrench, TestTubes, Plus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function FactoryDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, ordersRes] = await Promise.all([
          api.get('/factory/dashboard'),
          api.get('/factory/production-orders'),
        ]);
        setDashboard(dashRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch { toast.error('Failed to load factory data'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const STATUS_COLORS = {
    planned: 'bg-info/20 text-info',
    in_progress: 'bg-warning/20 text-warning',
    completed: 'bg-success/20 text-success',
    cancelled: 'bg-error/20 text-error',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="factory-dashboard-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Factory size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="factory-dashboard-title">Factory Dashboard</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Production, materials & quality overview</p>
            </div>
          </div>
          <Link to="/production-orders">
            <Button className="rounded-xl h-9 text-[13px]" data-testid="new-production-order-btn">
              <Plus size={14} className="mr-1.5" />New Order
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total Orders</p>
              <p className="text-2xl font-bold font-outfit text-foreground mt-1">{dashboard?.production.total || 0}</p>
              <div className="flex gap-2 mt-2 text-[10px]">
                <Badge className="bg-warning/20 text-warning text-[9px]">{dashboard?.production.in_progress || 0} active</Badge>
                <Badge className="bg-success/20 text-success text-[9px]">{dashboard?.production.completed || 0} done</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-cyan-500 uppercase tracking-[0.08em]">Machines</p>
              <p className="text-2xl font-bold font-outfit text-cyan-600 mt-1">{dashboard?.machines.total || 0}</p>
              <p className="text-[10px] text-muted-foreground mt-2">{dashboard?.machines.operational || 0} operational</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Raw Materials</p>
              <p className="text-2xl font-bold font-outfit text-foreground mt-1">{dashboard?.materials.total || 0}</p>
              {(dashboard?.materials.low_stock || 0) > 0 && (
                <Badge className="bg-error/20 text-error text-[9px] mt-2">{dashboard?.materials.low_stock} low stock</Badge>
              )}
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Quality Rate</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1">{dashboard?.quality.pass_rate || 0}%</p>
              <p className="text-[10px] text-muted-foreground mt-2">{dashboard?.quality.total_checks || 0} checks</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { path: '/production-orders', icon: ClipboardCheck, label: 'Production Orders', color: 'from-blue-500 to-blue-600' },
            { path: '/bill-of-materials', icon: Factory, label: 'Bill of Materials', color: 'from-violet-500 to-violet-600' },
            { path: '/raw-materials', icon: Boxes, label: 'Raw Materials', color: 'from-amber-500 to-amber-600' },
            { path: '/machines', icon: Wrench, label: 'Machines', color: 'from-cyan-500 to-cyan-600' },
            { path: '/quality-control', icon: TestTubes, label: 'Quality Control', color: 'from-emerald-500 to-emerald-600' },
          ].map(item => (
            <Link key={item.path} to={item.path}>
              <Card className="border-0 bg-white hover:shadow-md transition-all cursor-pointer group">
                <CardContent className="py-4 px-4 flex flex-col items-center text-center">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-2 shadow-sm`}>
                    <item.icon size={18} className="text-white" />
                  </div>
                  <p className="text-[12px] font-semibold text-foreground">{item.label}</p>
                  <ArrowRight size={12} className="mt-1 text-muted-foreground group-hover:text-primary transition-colors" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Production Orders */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
          <CardHeader className="pt-5 px-6 pb-0 flex flex-row items-center justify-between">
            <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">Recent Production Orders</CardTitle>
            <Link to="/production-orders" className="text-[12px] text-primary hover:underline font-medium">View all</Link>
          </CardHeader>
          <CardContent className="pt-4 px-0">
            {recentOrders.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                <Factory size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-[13px]">No production orders yet</p>
                <Link to="/production-orders"><Button size="sm" className="mt-3 rounded-xl text-[13px]"><Plus size={14} className="mr-1.5" />Create First Order</Button></Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="recent-orders-table">
                  <thead><tr>
                    <th className="text-left">Order #</th>
                    <th className="text-left">Product</th>
                    <th className="text-right">Qty</th>
                    <th className="text-center">Status</th>
                    <th className="text-left">Due Date</th>
                  </tr></thead>
                  <tbody>
                    {recentOrders.map(order => (
                      <tr key={order.id}>
                        <td className="font-mono text-[12px] font-semibold text-primary">{order.order_number}</td>
                        <td className="font-medium text-foreground">{order.product_name}</td>
                        <td className="text-right font-semibold tabular-nums">{order.quantity} {order.unit}</td>
                        <td className="text-center"><Badge className={`${STATUS_COLORS[order.status] || 'bg-muted'} text-[11px] capitalize`}>{order.status?.replace('_', ' ')}</Badge></td>
                        <td className="text-muted-foreground">{order.due_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
